/**
 * Блок голоса (§10).
 *
 *   Ваш голос готов
 *   [Прослушать]
 *   Качество: Хорошее
 *   [Улучшить голос]
 *
 * Предложение дополнить запись показывается ТОЛЬКО когда есть что улучшать:
 * лишнее предложение читается как «у вас плохо получилось» (§10.1).
 */
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  voiceDeficitLabels,
  type VoiceProfileView,
} from '@alterium/shared';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Card, CardBody } from '../ui/Card';
import { StatusDot } from '../ui/StatusDot';
import { voiceApi } from '../../api/voice';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';

export function VoiceCard({ voice }: { voice: VoiceProfileView }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['voice'] }),
      queryClient.invalidateQueries({ queryKey: ['persona'] }),
    ]);

  const start = useMutation({ mutationFn: voiceApi.start, onSuccess: refresh });
  const remove = useMutation({ mutationFn: voiceApi.remove, onSuccess: refresh });

  const ready = voice.status === 'ready' || voice.status === 'weak';

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <StatusDot
            tone={
              ready
                ? 'ok'
                : voice.status === 'failed'
                  ? 'fail'
                  : voice.status === 'not_started'
                    ? 'idle'
                    : 'warn'
            }
          />
          <span className="text-body text-content">{ready ? t('voice.ready') : voice.label}</span>
          {voice.qualityLabel ? (
            <span className="ml-auto text-meta text-content-muted">
              {t('voice.quality', { value: voice.qualityLabel })}
            </span>
          ) : null}
        </div>

        {ready && voice.previewUrl ? (
          <audio src={voice.previewUrl} controls className="w-full max-w-[420px]" />
        ) : null}

        {start.isError ? (
          <Alert tone="error">{errorText(start.error)}</Alert>
        ) : null}

        {/* §10.1: предложение дополнить запись — только когда есть что улучшать */}
        {ready && voice.improvementSuggested ? (
          <div className="rounded-control border border-line bg-surface-secondary px-4 py-3">
            <p className="text-body text-content">
              {t('voice.needMore', {
                count: Math.max(1, Math.round(voice.improvementSec / 60)),
              })}
            </p>
            {voice.deficits.length > 0 ? (
              <p className="mt-1 text-meta text-content-muted">
                {t('voice.deficits', {
                  list: voice.deficits.map((d) => voiceDeficitLabels[d]).join(', '),
                })}
              </p>
            ) : null}
            <div className="mt-3">
              <Button onClick={() => navigate('/persona/voice')}>{t('voice.improve.cta')}</Button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-3">
          {/*
            §10.1: «пользователь хочет улучшить результат» — такой же повод,
            как шум или нехватка длительности. Раньше попасть на дополнительную
            запись можно было, только если система сама сочла голос слабым:
            человек, которому хочется лучше, упирался в отсутствие кнопки.
          */}
          {ready && !voice.improvementSuggested ? (
            <Button variant="tertiary" onClick={() => navigate('/persona/voice')}>
              {t('voice.improve.cta')}
            </Button>
          ) : null}

          {voice.status === 'not_started' || voice.status === 'failed' ? (
            <Button variant="primary" onClick={() => start.mutate()} disabled={start.isPending}>
              {voice.status === 'failed' ? t('common.tryAgain') : t('voice.create')}
            </Button>
          ) : null}

          {ready ? (
            <Button
              className="border-error/50 text-error hover:border-error"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >{t('voice.delete')}</Button>
          ) : null}
        </div>

        {ready ? (
          <p className="text-meta text-content-muted">
            {t('voice.deleteWarning')}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
